import { useNavStack } from 'careand-redux/slices/ui/hooks';
import _ from 'lodash';
import { Stacks } from 'careand-redux/slices/ui/navigation';
import { useAppSelector } from 'careand-redux/store';
import {
  NavStack,
  NavStackRoutes,
  usePathParams,
  useQueryParams,
} from 'components/Navigation/NavStack';
import Icon from 'components/Utils/NewIcon';
import React, { ReactNode, useMemo, useState, useCallback, useEffect } from 'react';
import RouteUtils from 'services/RouteUtils';
import './style.scss';
import { ClinicInfo, Physician } from 'generated/api';
import { PhysicianImage } from 'components/Common/UserImage';
import PhysicianUtils from 'services/PhysicianUtils';
import StaffSection from './Sections/Staff';
import ClinicUtils from 'services/ClinicUtils';
import FinancialsSection, { type Range, RANGES } from './Sections/Financials';
import ClinicSection from './Sections/Clinic';
import { useLoadStatistics, StatisticsContext } from './context';
import { Permission, PERMISSIONS } from 'services/StaffUtils';
import TechnologySection from './Sections/Technology';
import ImmunizationInventorySection from './Sections/ImmunizationInventory';
import { ALLOW_ALL } from './components/DashboardBox';

export type FilterType = 'practitioner' | 'clinic' | 'all-clinics';

export type Filter = {
  type?: FilterType;
  id?: string;
  financialsRange?: Range;
  cmp?: string[];
};

type FilterCallback = (filter: Filter) => void;

const DashboardSection = {
  clinic: {
    name: 'Clinic Stats',
    icon: 'hospital',
    route: 'clinic',
    component: ClinicSection,
    filter: TestFilterList,
    permissionsRequired: ALLOW_ALL,
  },
  staff: {
    name: 'Staff Stats',
    icon: 'user-doctor',
    route: 'staff',
    component: StaffSection,
    filter: TestFilterList,
    permissionsRequired: ALLOW_ALL,
  },
  // skills: {
  //   name: 'Skills',
  //   icon: 'table-list',
  //   route: 'skills',
  //   component: null,
  //   filter: TestFilterList,
  // permissionsRequired: []
  // },
  technology: {
    name: 'Technology',
    icon: 'laptop-mobile',
    route: 'technology',
    component: TechnologySection,
    filter: TestFilterList,
    permissionsRequired: [PERMISSIONS.super_admin, PERMISSIONS.statistics] as Permission[],
  },
  financials: {
    name: 'Financials',
    icon: 'chart-line',
    route: 'financials',
    component: FinancialsSection,
    filter: FinancialsFilterList,
    permissionsRequired: [PERMISSIONS.super_admin, PERMISSIONS.statistics] as Permission[],
  },
  immunizationInventory: {
    name: 'Immunization Inventory',
    icon: 'syringe',
    route: 'immunization-inventory',
    component: ImmunizationInventorySection,
    filter: TestFilterList,
    permissionsRequired: [PERMISSIONS.super_admin, PERMISSIONS.statistics] as Permission[],
  },
} as const;

type DashboardSection = keyof typeof DashboardSection;
type DashboardSectionConfig = (typeof DashboardSection)[DashboardSection];

const StatisticsDashboard = () => {
  const data = useQueryParams() as { type?: string; id?: string; cmp?: string };
  const [filter, setFilter] = useState<Filter>({
    id: data.id,
    //TODO validate that is FilterType
    type: (data.type as FilterType) ?? 'all-clinics',
    cmp: JSON.parse(data.cmp ?? '[]'),
  });
  const { section } = usePathParams() as { section: DashboardSection };
  const permissions = useAppSelector((state) => state.authentication.permissions);

  const statistics = useLoadStatistics();

  const sectionRoutes = useMemo(() => {
    const acc: NavStackRoutes = {};
    Object.values(DashboardSection).forEach((section) => {
      const { permissionsRequired } = section;
      if (!hasPermission(permissionsRequired, permissions)) {
        return;
      }
      const Component = section.component;
      acc['*/' + section.route] = {
        element: Component ? <Component filter={filter} /> : null,
      };
    });
    return acc;
  }, [filter, permissions]);

  const onFilterChange: FilterCallback = useCallback((filter: Filter) => {
    const urlParams = new URLSearchParams();
    urlParams.set('type', filter.type!);
    if (filter.id) {
      urlParams.set('id', filter.id);
    }
    if (filter.financialsRange) {
      urlParams.set('financialsRange', filter.financialsRange);
    } else {
      urlParams.delete('financialsRange');
    }
    if (filter.cmp) {
      urlParams.set('cmp', JSON.stringify(filter.cmp));
    } else {
      urlParams.delete('cmp');
    }

    const url = new URL(window.location.href);
    url.search = urlParams.toString();
    window.history.replaceState(null, '', url.toString());
    setFilter(filter);
  }, []);

  useEffect(() => {
    if (section === 'financials' && !filter.financialsRange) {
      onFilterChange({ ...filter, financialsRange: 'All' });
    }
  }, [filter, section]);

  return (
    <div className="page statistics-dashboard">
      <DashboardSectionList />
      <div className="lhs">
        {section === 'financials' ? (
          <FinancialsFilterList filter={filter} onFilterChange={onFilterChange} />
        ) : (
          <TestFilterList filter={filter} onFilterChange={onFilterChange} />
        )}
      </div>
      <div className="content-area force-no-scrollbar">
        <StatisticsContext.Provider value={statistics}>
          <NavStack stack={Stacks.Master} authenticateAll routes={sectionRoutes} />
          {filter.cmp?.map((id) => {
            const Component = DashboardSection[section].component;
            return <Component filter={{ ...filter, id }} key={id} />;
          })}
        </StatisticsContext.Provider>
      </div>
    </div>
  );
};

export default StatisticsDashboard;

function DashboardSectionList() {
  const path = usePathParams() as { section?: string };
  const nav = useNavStack(Stacks.Master);
  const permissions = useAppSelector((state) => state.authentication.permissions);

  return (
    <div className="dashboard-section-list">
      {Object.entries(DashboardSection)
        .filter(([, section]) =>
          hasPermission(section.permissionsRequired as string[], permissions),
        )
        .map(([key, section]) => (
          <DashboardSectionButton
            section={section}
            key={key}
            isSelected={key === path.section}
            onClick={() => {
              const route = RouteUtils.getRoute(RouteUtils.routes().clinicStatisticsDashboard, {
                ':section': section.route,
              });
              nav.push(route);
            }}
          />
        ))}
    </div>
  );
}

function DashboardSectionButton({
  section,
  onClick,
  isSelected,
}: {
  section: DashboardSectionConfig;
  onClick: () => void;
  isSelected: boolean;
}) {
  return (
    <div className="dashboard-section-button" data-selected={isSelected} onClick={onClick}>
      <Icon src={section.icon} height="22px" />
      <span>{section.name}</span>
    </div>
  );
}

function FinancialsFilterList({
  filter,
  onFilterChange,
}: {
  filter: Filter;
  onFilterChange: FilterCallback;
}) {
  return (
    <div className="financials-filter-list">
      <TestFilterList clinicsOnly filter={filter} onFilterChange={onFilterChange} />
      <FinancialRangeFilterList filter={filter} onFilterChange={onFilterChange} />
    </div>
  );
}

function TestFilterList({
  filter,
  onFilterChange,
  clinicsOnly,
}: {
  filter: Filter;
  onFilterChange: FilterCallback;
  clinicsOnly?: boolean;
}) {
  const activeUserClinicId = useAppSelector((state) => state.authentication.user?.clinic_id);
  const clinics = useAppSelector((state) => state.resource.clinics);
  // A closed clinic isn't a filter any more (its practitioners and patients
  // moved on); the all-clinics charts still carry its history. A filter
  // already pointing at one (an old link) keeps its button.
  const selectedClinicId = filter.type === 'clinic' ? filter.id : undefined;
  const sortedClinics = useMemo(() => {
    return _.sortBy(ClinicUtils.getSelectableClinics(clinics, selectedClinicId), [
      (it) => it.id !== activeUserClinicId,
      (it) => it.name,
    ]);
  }, [clinics, activeUserClinicId, selectedClinicId]);
  const physicians = useAppSelector((state) => state.physicians.byId);
  const physiciansPerClinic = useMemo(() => {
    // A profile still naming a closed clinic lists under its replacement, so
    // it doesn't vanish with the closed clinic's button.
    return _(physicians)
      .filter((it) => !it?.inactive)
      .filter((it) => !it?.hide_portal)
      .filter((it) => !it?.intern_of)
      .groupBy((it) => ClinicUtils.resolveForwardedClinicId(it?.clinic_id, clinics))
      .mapValues((it) => _.sortBy(it, PhysicianUtils.getName))
      .value() as Partial<Record<string, Physician[]>>;
  }, [physicians, clinics]);

  // Picking a clinic or practitioner keeps the chosen financials range
  const onSelect = (update: Filter) =>
    onFilterChange({ ...update, financialsRange: filter.financialsRange });

  const onRightClick = (update: Filter) => {
    let cmp = [...(filter.cmp ?? [])];
    const idx = cmp.indexOf(update.id!);
    if (idx != -1) {
      cmp.splice(idx, 1);
    } else {
      cmp.push(update.id!);
    }

    onFilterChange({ ...filter, cmp });
  };

  return (
    <div className="test-filter-list force-no-scrollbar">
      <AllClinicsButton
        onClick={onSelect}
        onRightClick={onRightClick}
        isSelected={filter.type === 'all-clinics'}
        isSelectedForComparison={!!filter.cmp?.includes('all-clinics')}
      />
      {sortedClinics.map((clinic) => (
        <>
          <ClinicFilterButton
            clinic={clinic}
            key={clinic.id}
            onClick={onSelect}
            onRightClick={onRightClick}
            isSelected={filter.type === 'clinic' && filter.id === clinic.id}
            isSelectedForComparison={!!filter.cmp?.includes(clinic.id!)}
          />
          {!clinicsOnly &&
            physiciansPerClinic[clinic.id!]?.map((physician) => (
              <PractitionerFilterButton
                physician={physician}
                key={physician.id}
                onClick={onSelect}
                onRightClick={onRightClick}
                isSelected={filter.type === 'practitioner' && filter.id === physician.id}
                isSelectedForComparison={!!filter.cmp?.includes(physician.id)}
              />
            ))}
        </>
      ))}
    </div>
  );
}

function FinancialRangeFilterList({
  filter,
  onFilterChange,
}: {
  filter: Filter;
  onFilterChange: FilterCallback;
}) {
  return (
    <div className="test-filter-list financials-filter-list force-no-scrollbar">
      {RANGES.map((range) => (
        <FinancialRangeFilterButton
          range={range.name}
          onClick={() => onFilterChange({ ...filter, financialsRange: range.name })}
          isSelected={filter.financialsRange === range.name}
        />
      ))}
    </div>
  );
}

function AllClinicsButton({
  onClick,
  onRightClick,
  isSelected,
  isSelectedForComparison,
}: {
  onClick: FilterCallback;
  onRightClick: FilterCallback;
  isSelected: boolean;
  isSelectedForComparison: boolean;
}) {
  return (
    <FilterButton
      className="clinic-filter-button"
      image={
        <div className="icon-wrapper w-full h-full grid place-items-center">
          <Icon src="hospital" width="20px" />
        </div>
      }
      name="All Clinics"
      onClick={() => onClick({ type: 'all-clinics' })}
      onRightClick={() => onRightClick({ type: 'all-clinics' })}
      isSelected={isSelected}
      isSelectedForComparison={isSelectedForComparison}
    />
  );
}
function ClinicFilterButton({
  clinic,
  onClick,
  onRightClick,
  isSelected,
  isSelectedForComparison,
}: {
  clinic: ClinicInfo;
  onClick: FilterCallback;
  onRightClick: FilterCallback;
  isSelected: boolean;
  isSelectedForComparison: boolean;
}) {
  return (
    <FilterButton
      className="clinic-filter-button"
      image={
        <div className="icon-wrapper w-full h-full grid place-items-center">
          {ClinicUtils.getInitials(clinic)}
        </div>
      }
      name={clinic.name!}
      onClick={() => onClick({ type: 'clinic', id: clinic.id })}
      onRightClick={() => onRightClick({ type: 'clinic', id: clinic.id })}
      isSelected={isSelected}
      isSelectedForComparison={isSelectedForComparison}
    />
  );
}

function PractitionerFilterButton({
  physician,
  onClick,
  onRightClick,
  isSelected,
  isSelectedForComparison,
}: {
  physician: Physician;
  onClick: FilterCallback;
  onRightClick: FilterCallback;
  isSelected: boolean;
  isSelectedForComparison: boolean;
}) {
  return (
    <FilterButton
      image={<PhysicianImage user={physician} size={40} />}
      name={PhysicianUtils.getName(physician)!}
      onClick={() => onClick({ type: 'practitioner', id: physician.id })}
      onRightClick={() => onRightClick({ type: 'practitioner', id: physician.id })}
      isSelected={isSelected}
      isSelectedForComparison={isSelectedForComparison}
    />
  );
}

function FilterButton({
  className,
  image,
  name,
  onClick,
  onRightClick,
  isSelected,
  isSelectedForComparison,
}: {
  className?: string;
  image: ReactNode;
  name: string;
  onClick: () => void;
  onRightClick?: () => void;
  isSelected: boolean;
  isSelectedForComparison?: boolean;
}) {
  return (
    <div
      className={`filter-button flex items-center min-w-[40px] ${className ?? ''}`}
      onClick={onClick}
      onContextMenu={(ev) => {
        ev.preventDefault();
        onRightClick?.();
      }}
      data-selected={isSelected}
      data-cmp={isSelectedForComparison}
    >
      <div className="img-container min-w-[40px] aspect-square rounded-full overflow-hidden mr-2">
        {image}
      </div>
      <span className="truncate">{name}</span>
    </div>
  );
}

function FinancialRangeFilterButton({
  range,
  onClick,
  isSelected,
}: {
  range: Range;
  onClick: () => void;
  isSelected: boolean;
}) {
  const rangeName = useMemo(() => RANGES.find((it) => it.name === range)?.fullName!, [range]);

  return (
    <FilterButton
      className="range-filter-button"
      image={<div>{range}</div>}
      name={rangeName}
      isSelected={isSelected}
      onClick={onClick}
    />
  );
}

function hasPermission(required: string[], available: string[]) {
  if (!required || required.length === 0) {
    return true;
  }
  // Create a set from the intersection of required and available permissions
  const intersection = new Set(required.filter((perm) => available.includes(perm)));
  return intersection.size > 0;
}
